import React, { createContext, useContext, useMemo } from 'react';

import { useLevelFocus, type UseLevelFocusResult } from '@/hooks/useLevelFocus';

const LevelFocusContext = createContext<UseLevelFocusResult | null>(null);

interface LevelFocusProviderProps {
  children: React.ReactNode;
}

/**
 * Provides the level practice is served at to a page subtree.
 *
 * It is mounted only on `/practice` (including `/tasks`) and `/progress`.
 * Per-page instances remain consistent because the underlying hook shares one
 * react-query cache entry; unrelated routes pay no request.
 */
export const LevelFocusProvider: React.FC<LevelFocusProviderProps> = ({
  children,
}) => {
  const { isLoading, value, ownLevelStream } = useLevelFocus();
  const memoized = useMemo(
    () => ({ isLoading, value, ownLevelStream }),
    [isLoading, value, ownLevelStream]
  );
  return (
    <LevelFocusContext.Provider value={memoized}>
      {children}
    </LevelFocusContext.Provider>
  );
};

/**
 * Without a provider, resolve to the own-pair default. This keeps surfaces
 * that carry no level and isolated tests inert.
 */
export const useLevelFocusContext = (): UseLevelFocusResult => {
  const ctx = useContext(LevelFocusContext);
  if (ctx) return ctx;
  return { isLoading: false, value: null, ownLevelStream: null };
};
